/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
import useWorkloadDetailsStore from '@/store/modules/pinia/workload-details';
import useWorkloadVersionStore from '@/store/modules/pinia/workload-version';
import useRegistryStore from '@/store/modules/pinia/registry';
import useNodesStore from '@/store/modules/pinia/nodes';
import useDnaStore from '@/store/modules/pinia/dna';
import useNodeUpdateLogStore from '@/store/modules/pinia/node-update-log';
import useNodeUpdateDetailsStore from '@/store/modules/pinia/node-update-details';
import useDeployDetailsStore from '@/store/modules/pinia/deploy-details';
import useDeployLogStore from '@/store/modules/pinia/deploy-log';
import useAuthStore from '@/store/modules/pinia/auth';
import useRemoteConnectionStore from '@/store/modules/pinia/remote-connection';
import useVmBackupsStore from '@/store/modules/pinia/vm-backups';
import useVmSnapshotStore from '@/store/modules/pinia/vm-snapshot';
import useAccessTokensStore from '@/store/modules/pinia/access-tokens';

const handleDockerRegistryEvents = (message) => {
  const workloadDetailsStore = useWorkloadDetailsStore();
  const workloadVersionStore = useWorkloadVersionStore();
  const registryStore = useRegistryStore();

  workloadDetailsStore.handleDockerRegistryEvent(message);
  workloadVersionStore.handleDockerRegistryEvent(message);
  registryStore.handleDockerRegistryEvents(message);
};

const handleNodeLifecycleEvents = (message) => {
  const nodesStore = useNodesStore();
  nodesStore.handleNodeLifecycleEvents(message);
};

const handleNodePersistentDataEvent = (message) => {
  const nodesStore = useNodesStore();
  nodesStore.mqttSelectedNodeUpdated(message);
};

const handleDnaStatusEvent = (message) => {
  const dnaStore = useDnaStore();
  const nodesStore = useNodesStore();
  const serialNumberOfNode = nodesStore.selectedNode.serialNumber;
  if (message.sender.split('/')[1] !== serialNumberOfNode) {
    return;
  }

  dnaStore.mqttDna(message.params);
};

const handleWorkloadEvents = async (message) => {
  const workloadDetailsStore = useWorkloadDetailsStore();
  const nodesStore = useNodesStore();

  workloadDetailsStore.handleWorkloadEvents(message);
  nodesStore.handleWorkloadEvents(message);
};

const handleSelectedNodeDeployedWlUpdatedEvent = (message) => {
  const nodesStore = useNodesStore();
  nodesStore.mqttSelectedNodeDeployedWlUpdated(message);
};

const handleBomCampaignCloudEvents = (message) => {
  const deployDetailsStore = useDeployDetailsStore();
  const { name, params } = message;
  if (
    name === 'all-admins-bom-campaign-created' ||
    name === 'all-admins-bom-campaign-updated-finished' ||
    name === 'all-admins-bom-campaign-updated'
  ) {
    if (params.campaignType === 'node_update') {
      const nodeUpdateLogStore = useNodeUpdateLogStore();
      nodeUpdateLogStore.handleBomServiceUpdateEvents(message);
    }
    if (params.campaignType === 'workload') {
      const deployLogStore = useDeployLogStore();
      deployLogStore.handleDeployBomCampaignEvent(message);
      deployDetailsStore.handleDeployBomCampaignEvent(message);
    }
  }
  if (name === 'all-admins-bom-task-updated') {
    const nodeUpdateDetailsStore = useNodeUpdateDetailsStore();
    if (params.campaignType === 'node_update') {
      nodeUpdateDetailsStore.updateTaskStatus(message);
    }
    if (params.campaignType === 'workload') {
      deployDetailsStore.handleDeployBomCampaignEvent(message);
    }
  }
};

const handleAccessTokenEvents = (message) => {
  if (message.msg !== 'accessTokenUpdated') {
    return;
  }

  const authStore = useAuthStore();
  const tokenUserId = message.token?.userId;

  // handle access token update event only if the token belongs to the logged in user
  if (tokenUserId && tokenUserId !== authStore.user?.id) {
    return;
  }

  const accessTokensStore = useAccessTokensStore();
  accessTokensStore.handleAccessTokenUpdatedEvent(message.token);
};

const handleAuthAndCrmUserEvents = (message) => {
  const authStore = useAuthStore();
  const { logout } = authStore.handleAuthAndCrmUserEvents(message);
  if (!logout) {
    return;
  }
  window.dispatchEvent(new CustomEvent('logout-required'));
};

const handleRemoteConnectionEvents = (message) => {
  const remoteConnectionStore = useRemoteConnectionStore();
  remoteConnectionStore.handleMqttRemoteConnectionResponse(message);
};

const handleBackupLogEvent = (message) => {
  const vmBackupsStore = useVmBackupsStore();
  vmBackupsStore.mqttBackups(message);
};

const handleSnapshotEvent = (message) => {
  const vmSnapshotStore = useVmSnapshotStore();
  vmSnapshotStore.mqttSnapshot(message);
};

const handleNodeInformationUpdateEvent = (message) => {
  const nodesStore = useNodesStore();
  const remoteConnectionStore = useRemoteConnectionStore();
  nodesStore.mqttAnyNodeUpdated(message);
  remoteConnectionStore.mqttRemoteConnectionsUpdated({
    ...(nodesStore.selectedNode?._id && { nodeId: nodesStore.selectedNode._id }),
    message,
  });
};

export {
  handleDockerRegistryEvents,
  handleNodeLifecycleEvents,
  handleNodePersistentDataEvent,
  handleDnaStatusEvent,
  handleWorkloadEvents,
  handleNodeInformationUpdateEvent,
  handleSelectedNodeDeployedWlUpdatedEvent,
  handleBomCampaignCloudEvents,
  handleAccessTokenEvents,
  handleAuthAndCrmUserEvents,
  handleRemoteConnectionEvents,
  handleBackupLogEvent,
  handleSnapshotEvent,
};
